import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Plus, Trash2, Download, Pencil, Check, X } from 'lucide-react';
import { productCodesApi, type ProductCode } from '../../api/endpoints/productCodes';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import { Select } from '../../components/ui/Select';
import { SearchInput } from '../../components/ui/SearchInput';
import { StatCard } from '../../components/ui/StatCard';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { useToast } from '../../components/ui/Alert';
import { useDebounce } from '../../hooks/useDebounce';
import { formatDate } from '../../lib/formatters';

export function ProductCodeList() {
  const { storeId, productId } = useParams<{ storeId: string; productId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [codesText, setCodesText] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingCode, setEditingCode] = useState<{ id: string; value: string } | null>(null);
  const [deleteCodeTarget, setDeleteCodeTarget] = useState<ProductCode | null>(null);

  // Reset pagination-related state when search changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [debouncedSearch]);

  const { data, isLoading } = useQuery({
    queryKey: ['productCodes', storeId, productId, statusFilter, debouncedSearch],
    queryFn: () => productCodesApi.list(storeId!, productId!, {
      status: statusFilter !== 'all' ? statusFilter : undefined,
      search: debouncedSearch || undefined,
    }),
    enabled: !!storeId && !!productId,
  });

  const addMutation = useMutation({
    mutationFn: (codes: string[]) => productCodesApi.add(storeId!, productId!, codes),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['productCodes', storeId, productId] });
      toast('success', t('productCodes.addSuccess', { count: result.addedCount }));
      setShowAddModal(false);
      setCodesText('');
    },
    onError: () => toast('error', t('common.error')),
  });

  const updateCodeMutation = useMutation({
    mutationFn: ({ codeId, value }: { codeId: string; value: string }) =>
      productCodesApi.updateCode(storeId!, productId!, codeId, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productCodes', storeId, productId] });
      toast('success', t('common.success'));
      setEditingCode(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const deleteMutation = useMutation({
    mutationFn: (codeIds: string[]) => productCodesApi.delete(storeId!, productId!, codeIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productCodes', storeId, productId] });
      toast('success', t('common.success'));
      setSelectedIds(new Set());
    },
    onError: () => toast('error', t('common.error')),
  });

  const handleExport = async () => {
    try {
      const result = await productCodesApi.exportCsv(storeId!, productId!, statusFilter !== 'all' ? statusFilter : undefined);
      const blob = new Blob([result.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast('error', t('common.error'));
    }
  };

  const handleAddCodes = () => {
    const codes = codesText
      .split('\n')
      .map((c) => c.trim())
      .filter(Boolean);
    if (codes.length === 0) return;
    addMutation.mutate(codes);
  };

  const statusVariant = (status: string) => {
    if (status === 'available') return 'success';
    if (status === 'reserved') return 'warning';
    return 'info';
  };

  const handleSaveEdit = () => {
    if (!editingCode || !editingCode.value.trim()) return;
    updateCodeMutation.mutate({ codeId: editingCode.id, value: editingCode.value.trim() });
  };

  const columns: Column<ProductCode>[] = [
    {
      key: 'value',
      header: t('productCodes.code'),
      render: (c) => {
        if (editingCode?.id === c._id) {
          return (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                className="rounded border border-accent bg-bg-field px-2 py-1 font-mono text-sm outline-none w-full"
                value={editingCode.value}
                onChange={(e) => setEditingCode({ ...editingCode, value: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveEdit();
                  if (e.key === 'Escape') setEditingCode(null);
                }}
              />
              <button onClick={handleSaveEdit} className="rounded p-1 text-success hover:bg-success/10" disabled={updateCodeMutation.isPending}>
                <Check className="h-4 w-4" />
              </button>
              <button onClick={() => setEditingCode(null)} className="rounded p-1 text-text-muted hover:bg-bg-hover">
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        }
        return (
          <div className="flex items-center gap-1.5 group">
            <span className="font-mono text-sm">{c.value}</span>
            {c.status === 'available' && (
              <button
                onClick={(e) => { e.stopPropagation(); setEditingCode({ id: c._id, value: c.value }); }}
                className="rounded p-0.5 text-text-muted opacity-0 group-hover:opacity-100 hover:text-accent transition-opacity"
                title={t('common.edit')}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (c) => <Badge variant={statusVariant(c.status)}>{t(`productCodes.${c.status}`, c.status)}</Badge>,
    },
    {
      key: 'buyer',
      header: t('productCodes.buyer', 'Buyer'),
      render: (c) => {
        if (!c.buyer) return <span className="text-xs text-text-muted">-</span>;
        const label = c.buyer.username || c.buyer.email || c.buyer.phone || '-';
        return <span className="text-sm text-text-secondary">{label}</span>;
      },
    },
    {
      key: 'orderId',
      header: t('productCodes.order', 'Order'),
      render: (c) => {
        if (!c.orderId) return <span className="text-xs text-text-muted">-</span>;
        const ref = c.orderRef || c.orderId;
        return (
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/${storeId}/orders/${c.orderId}`); }}
            className="text-sm text-accent hover:underline"
          >
            {ref}
          </button>
        );
      },
    },
    {
      key: 'reservedAt',
      header: t('common.date'),
      render: (c) => <span className="text-xs text-text-secondary">{c.reservedAt ? formatDate(c.reservedAt) : '-'}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (c) =>
        c.status === 'available' ? (
          <PermissionGate requires="products.edit">
            <button
              onClick={(e) => { e.stopPropagation(); setDeleteCodeTarget(c); }}
              className="rounded p-1.5 text-text-muted opacity-0 group-hover:opacity-100 hover:bg-danger/10 hover:text-danger transition-all"
              title={t('common.delete')}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </PermissionGate>
        ) : null,
    },
  ];

  const codes = data?.codes ?? [];
  const stats = data?.stats;
  const product = data?.product;

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate(`/${storeId}/products`)}
          className="rounded-lg p-2 text-text-muted hover:bg-bg-hover hover:text-text-primary"
        >
          <ArrowRight className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold">{t('productCodes.title')}</h1>
          {product && <p className="text-sm text-text-muted">{product.title}</p>}
        </div>
      </div>

      {stats && (
        <div className="mb-6 grid gap-4 sm:grid-cols-4">
          <StatCard title={t('common.total')} value={stats.total} />
          <StatCard title={t('productCodes.available')} value={stats.available} />
          <StatCard title={t('productCodes.reserved')} value={stats.reserved} />
          <StatCard title={t('productCodes.revealed')} value={stats.revealed} />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('common.search') + '...'}
          className="w-full sm:w-64"
        />
        <Select
          options={[
            { value: 'all', label: t('common.all') },
            { value: 'available', label: t('productCodes.available') },
            { value: 'reserved', label: t('productCodes.reserved') },
            { value: 'revealed', label: t('productCodes.revealed') },
          ]}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-40"
        />
        <div className="flex-1" />
        <Button variant="secondary" size="sm" onClick={handleExport}>
          <Download className="h-4 w-4" />
          {t('common.export')}
        </Button>
        {selectedIds.size > 0 && (
          <PermissionGate requires="products.edit">
            <Button
              variant="danger"
              size="sm"
              onClick={() => deleteMutation.mutate(Array.from(selectedIds))}
              isLoading={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
              {t('common.delete')} ({selectedIds.size})
            </Button>
          </PermissionGate>
        )}
        <PermissionGate requires="products.edit">
          <Button size="sm" onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4" />
            {t('productCodes.addCodes')}
          </Button>
        </PermissionGate>
      </div>

      <div className="rounded-xl border border-border-default bg-bg-secondary">
        <DataTable
          columns={columns}
          data={codes}
          isLoading={isLoading}
          keyExtractor={(c) => c._id}
          emptyTitle={t('common.noResults')}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </div>

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={t('productCodes.addCodes')}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleAddCodes} isLoading={addMutation.isPending}>{t('common.save')}</Button>
          </>
        }
      >
        <Textarea
          label={t('productCodes.bulkAdd')}
          rows={10}
          placeholder={t('productCodes.bulkPlaceholder')}
          value={codesText}
          onChange={(e) => setCodesText(e.target.value)}
        />
        <p className="text-xs text-text-muted mt-2">{t('productCodes.addHint', 'New codes will be added alongside existing ones. Purchased codes are not affected.')}</p>
      </Modal>

      {/* Per-row delete confirmation */}
      <Modal
        isOpen={!!deleteCodeTarget}
        onClose={() => setDeleteCodeTarget(null)}
        title={t('common.confirm')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteCodeTarget(null)}>{t('common.cancel')}</Button>
            <Button
              variant="danger"
              isLoading={deleteMutation.isPending}
              onClick={() => {
                if (deleteCodeTarget) {
                  deleteMutation.mutate([deleteCodeTarget._id], {
                    onSuccess: () => setDeleteCodeTarget(null),
                  });
                }
              }}
            >
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">{t('productCodes.deleteConfirm', 'Are you sure you want to delete this code?')}</p>
        <p className="mt-2 font-mono text-sm font-medium">{deleteCodeTarget?.value}</p>
      </Modal>
    </div>
  );
}
